import { Classification } from '@synergy/models';
import { ConfirmationDialog, PoolFormattedMessage } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PaloListView } from '@synergy/components-palo';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as ClassificationsActions from './store/classifications-actions';
import { classificationsDeleteErrorSelector, classificationsSelector } from './store/classifications-selector';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface ClassificationsOverviewProps {
    onEdit: (unit: Classification) => void;
}

export const ClassificationsOverview = (props: ClassificationsOverviewProps) => {
    const dispatch = useThunkDispatch();
    const classifications = useAppSelector(classificationsSelector);
    const deleteErrorClassification = useAppSelector(classificationsDeleteErrorSelector);
    const [searchParams, setSearchParams] = useSearchParams();

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };
    const [deleteModalData, setDeleteModalData] = useState<Classification | null>(null);

    useEffect(() => {
        dispatch(ClassificationsActions.fetchClassifications(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (!deleteModalData?.id || deleteModalData.id < 0) {
            return;
        }
        dispatch(ClassificationsActions.deleteClassification(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<Classification, number>
                embedded={true}
                columns={[
                    {
                        key: 'name',
                        type: 'name',
                        head: <FormattedMessage id="app.table.column.name" />,
                        cell: classification => classification.name,
                    },
                ]}
                items={classifications.items}
                getRowId={classification => classification.id}
                emptyLabel="Keine Klassifizierungen."
                rowActions={classification => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(classification) },
                    {
                        label: <FormattedMessage id="app.common.delete" />,
                        onClick: () => setDeleteModalData(classification),
                    },
                ]}
                pagination={{
                    count: classifications.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />

            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.configuration.classifications.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleDelete}
                onCancel={() => setDeleteModalData(null)}
            >
                <PoolFormattedMessage
                    id="app.configuration.classifications.delete.text"
                    values={{ name: deleteModalData?.name }}
                />
            </ConfirmationDialog>

            <ConfirmationDialog
                isVisible={!!deleteErrorClassification}
                title={<FormattedMessage id="app.configuration.classifications.delete.error" />}
                isSuccessDialog={true}
                action={<FormattedMessage id="app.common.okAgreed" />}
                onConfirm={() => dispatch(ClassificationsActions.clearDeleteError())}
            >
                <PoolFormattedMessage
                    id="app.configuration.classifications.delete.error.text"
                    values={{
                        name: deleteErrorClassification?.name,
                    }}
                />
            </ConfirmationDialog>
        </>
    );
};
